import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../LanguageContext';
import { useHistory } from 'react-router-dom';
import './index.css';
import { transcripts } from "./transcripts";
import {
  highlightMatch,
  estimateTimeFromWordPosition,
} from "../common/searchUtils";

const videos = Array.from({ length: 20 }, (_, i) => ({
  id: `video-${i + 1}`,
  titleKey: `flm.forward.video${i + 1}#title`,
  transcriptKey: `flm.forward.video${i + 1}#transcript`,
  videoUrl: "https://www.w3schools.com/html/mov_bbb.mp4",
  thumbnail:
    i % 2 === 0
      ? "/assets/images/full-life-music/compact-black.png"
      : "/assets/images/full-life-music/compact-white.png",
}));

export function VideosGrid() {
  const { t } = useLanguage();
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingVideoId, setLoadingVideoId] = useState(null);
  const [filteredVideos, setFilteredVideos] = useState(videos);
  const searchInputRef = useRef(null);
  const videoRefs = useRef({});
  const [videoDurations, setVideoDurations] = useState({});

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const filtered = videos.filter((video) => {
      const title = t(video.titleKey).toLowerCase();
      const description = video.description?.toLowerCase() || "";
      const transcript = t(video.transcriptKey, transcripts);
      const searchTermLower = searchTerm.toLowerCase();

      const matches = [];

      if (
        title.includes(searchTermLower) ||
        description.includes(searchTermLower)
      ) {
        matches.push({ type: "metadata", time: 0 });
      }

      if (transcript.toLowerCase().includes(searchTermLower)) {
        const duration = videoDurations[video.id] || 300; // fallback to 300s if duration not yet known
        const estimatedTime = estimateTimeFromWordPosition(
          transcript,
          searchTermLower,
          duration
        );
        matches.push({
          type: "transcript",
          time: estimatedTime,
          text: transcript.substr(
            // Calculate starting index for match, subtracting 50 from the index of the first
            // occurrence to provide context by including 50 characters before the search term.
            Math.max(0, transcript.toLowerCase().indexOf(searchTermLower) - 50),
            // Limit the length of the match + context to 100 characters
            100
          ),
        });
      }

      video.searchMatches = matches;
      return matches.length > 0;
    });
    setFilteredVideos(filtered);
  }, [searchTerm, t, videoDurations]);

  const handleVideoClick = (video) => {
    const firstMatch = video.searchMatches?.[0];
    const startTime = firstMatch?.time || 0;
    history.push(
      `${process.env.PUBLIC_URL}/flm/forward/${video.id}?t=${startTime}`
    );
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleVideoHoverIn = (video) => {
    const videoEl = videoRefs.current[video.id];
    if (videoEl) {
      setLoadingVideoId(video.id);
      videoEl.style.opacity = "1";
      videoEl.src = video.videoUrl;

      // Add metadata loaded event listener
      videoEl.addEventListener(
        "loadedmetadata",
        () => {
          setVideoDurations((prev) => ({
            ...prev,
            [video.id]: videoEl.duration,
          }));
        },
        { once: true }
      );

      videoEl.load();
      videoEl.play().catch(() => {
        setLoadingVideoId(null);
      });
    }
  };

  const handleVideoHoverOut = (video) => {
    const videoEl = videoRefs.current[video.id];
    if (videoEl) {
      setLoadingVideoId(null);
      videoEl.style.opacity = "0";
      videoEl.pause();
      videoEl.removeAttribute("src");
      videoEl.load();
    }
  };

  return (
    <div className="videos-container">
      <div className="videos-header">
        <h2>{t("flm.forward#title")}</h2>
        <div className="search-container">
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder={t("flm.forward#searchPlaceholder")}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <button
              className="clear-search"
              onClick={() => {
                setSearchTerm("");
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="videos-grid">
        {filteredVideos.map((video) => (
          <div className="video-item" key={video.id}>
            <div
              className="thumbnail-card"
              onClick={() => handleVideoClick(video)}
              onMouseEnter={(e) => handleVideoHoverIn(video)}
              onMouseLeave={(e) => handleVideoHoverOut(video)}
            >
              <img
                src={video.thumbnail}
                alt={t(video.titleKey)}
                className="thumbnail-image"
              />
              <video
                ref={(el) => (videoRefs.current[video.id] = el)}
                className="preview-video"
                muted
                loop
                playsInline
                preload="none"
                onPlaying={() => setLoadingVideoId(null)}
                onError={() => setLoadingVideoId(null)}
              />
              {loadingVideoId === video.id && (
                <div className="loading-spinner">
                  <svg className="spinner-svg" viewBox="0 0 50 50">
                    <circle
                      className="spinner-circle"
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      strokeWidth="5"
                    ></circle>
                  </svg>
                </div>
              )}
              <div className="video-title">{t(video.titleKey)}</div>
            </div>
            {searchTerm && (
              <div className="video-transcript-preview">
                {highlightMatch(
                  t(video.transcriptKey, transcripts),
                  searchTerm
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
} 